import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Key, Eye, EyeSlash, Copy, CheckCircle } from '@phosphor-icons/react';

/**
 * Admin-only password reset modal.
 *
 * Props:
 *   • open, onOpenChange  — controlled Dialog visibility
 *   • userId, userName, userEmail, userRole  — target user metadata (read-only)
 *
 * Flow (per user preference "B = manual only"):
 *   • Admin types the new password (min 6 chars, confirm must match)
 *   • Backend hashes + saves. On success we show a big prominent copy box
 *     with the plaintext so admin can hand it over verbally / on chat.
 */
export default function PasswordResetDialog({
  open, onOpenChange, userId, userName, userEmail, userRole,
}) {
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedPw, setSavedPw] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset state on close
      setPw1(''); setPw2(''); setShow(false); setSaving(false);
      setSavedPw(''); setCopied(false);
    }
  }, [open]);

  const submit = async () => {
    if (pw1.length < 6) return toast.error('Password kam se kam 6 chars ka do');
    if (pw1 !== pw2) return toast.error('Dono password match nahi kar rahe');
    setSaving(true);
    try {
      const { data } = await api.post('/auth/admin-reset-password', {
        user_id: userId,
        new_password: pw1,
      });
      setSavedPw(data?.new_password || pw1);
      toast.success('Password reset ho gaya — user ko share kar do');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Reset fail');
    } finally { setSaving(false); }
  };

  const copyPw = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(savedPw);
      } else {
        const ta = document.createElement('textarea');
        ta.value = savedPw;
        ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        ta.remove();
      }
      setCopied(true);
      toast.success('Password copied');
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error('Copy fail'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="password-reset-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Key size={18} weight="duotone" className="text-amber-600" />
            Reset Password
          </DialogTitle>
          <DialogDescription className="text-xs">
            {userName || '—'} · <span className="font-mono">{userEmail || '—'}</span>
            {userRole && (
              <span className="ml-1 text-[10px] uppercase font-semibold bg-slate-100 text-slate-700 rounded px-1.5 py-0.5">
                {userRole.replace(/_/g, ' ')}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {savedPw ? (
          // ─── SUCCESS STATE — show password for one-time copy ───
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
              <p className="text-xs font-semibold text-emerald-900 mb-1.5">
                ✅ Password reset ho gaya. Ye password user ko share karo:
              </p>
              <div className="flex items-center gap-2 mt-2">
                <code
                  className="flex-1 bg-white border border-emerald-300 rounded px-3 py-2 text-base font-mono font-bold text-emerald-900 tracking-wider select-all"
                  data-testid="reset-password-value"
                >
                  {savedPw}
                </code>
                <Button
                  onClick={copyPw}
                  size="sm"
                  className={copied ? 'bg-emerald-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                  data-testid="reset-password-copy-btn"
                >
                  {copied ? <><CheckCircle size={14} className="mr-1" /> Copied</> : <><Copy size={14} className="mr-1" /> Copy</>}
                </Button>
              </div>
            </div>
            <p className="text-[10px] text-gray-500">
              ⚠️ Ye password sirf ek baar dikh raha hai. Copy karke turant user ko de do — dubara nahi milega.
            </p>
          </div>
        ) : (
          // ─── ENTRY STATE — admin types new password ───
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">New Password</label>
              <div className="relative mt-1">
                <Input
                  type={show ? 'text' : 'password'}
                  value={pw1}
                  onChange={(e) => setPw1(e.target.value)}
                  placeholder="Min 6 characters"
                  className="pr-9"
                  autoFocus
                  data-testid="reset-pw-input-1"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Toggle visibility"
                >
                  {show ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700">Confirm Password</label>
              <Input
                type={show ? 'text' : 'password'}
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                placeholder="Same password again"
                className="mt-1"
                data-testid="reset-pw-input-2"
              />
              {pw2 && pw1 !== pw2 && (
                <p className="text-[10px] text-rose-600 mt-1">Match nahi kar raha</p>
              )}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-900">
              User ka existing password overwrite ho jayega. Ye action audit log me record hoga.
            </div>
          </div>
        )}

        <DialogFooter>
          {savedPw ? (
            <Button onClick={() => onOpenChange(false)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="reset-close-btn">
              Done
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
              <Button
                onClick={submit}
                disabled={saving || pw1.length < 6 || pw1 !== pw2}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="reset-save-btn"
              >
                {saving ? 'Saving…' : 'Reset Password'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
